/**
 * Recurring Schedule Service
 * Handles RecurringSchedules collection operations for web dashboard
 */

import {
  collection,
  doc,
  getDoc,
  getDocsFromServer,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { RecurringSchedule, RecurrenceFrequency } from '../../../shared/types';
import { db, functions } from '../config/firebase';
import { convertDocumentTimestamps } from '@shared/utils/firestore';
import { formatRecurrence as sharedFormatRecurrence } from '@shared/utils/formatting';
import { DAY_NAMES_FULL } from '../constants/schedule';

export interface RecurringScheduleWithId extends RecurringSchedule {
  id: string;
}

/**
 * Get all recurring schedules for a company
 */
export const getCompanySchedules = async (companyId: string): Promise<RecurringScheduleWithId[]> => {
  const q = query(
    collection(db, 'RecurringSchedules'),
    where('companyId', '==', companyId)
  );
  const snapshot = await getDocsFromServer(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...convertDocumentTimestamps<RecurringSchedule>(doc.data()),
  })) as RecurringScheduleWithId[];
};

/**
 * Get recurring schedules assigned to a specific manager
 */
export const getManagerSchedules = async (managerId: string): Promise<RecurringScheduleWithId[]> => {
  const q = query(
    collection(db, 'RecurringSchedules'),
    where('assignedManagerId', '==', managerId)
  );
  const snapshot = await getDocsFromServer(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...convertDocumentTimestamps<RecurringSchedule>(doc.data()),
  })) as RecurringScheduleWithId[];
};

/**
 * Get active recurring schedules for a company
 */
export const getActiveSchedules = async (companyId: string): Promise<RecurringScheduleWithId[]> => {
  const q = query(
    collection(db, 'RecurringSchedules'),
    where('companyId', '==', companyId),
    where('isActive', '==', true)
  );
  const snapshot = await getDocsFromServer(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...convertDocumentTimestamps<RecurringSchedule>(doc.data()),
  })) as RecurringScheduleWithId[];
};

/**
 * Get a single recurring schedule by ID
 */
export const getScheduleById = async (scheduleId: string): Promise<RecurringScheduleWithId | null> => {
  const docRef = doc(db, 'RecurringSchedules', scheduleId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...convertDocumentTimestamps<RecurringSchedule>(docSnap.data()),
  } as RecurringScheduleWithId;
};

/**
 * Create a new recurring schedule (via Cloud Function)
 */
export const createRecurringSchedule = async (data: {
  companyId: string;
  title: string;
  description?: string;
  location: {
    address: string;
    latitude: number;
    longitude: number;
  };
  hourlyRate: number;
  recurrenceFrequency: RecurrenceFrequency;
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  effectiveFrom: Date;
  effectiveUntil?: Date;
  assignedManagerId?: string;
  userIds?: string[];
}): Promise<string> => {
  const createScheduleFn = httpsCallable(functions, 'createRecurringSchedule');
  const result = await createScheduleFn({
    ...data,
    effectiveFrom: { seconds: Math.floor(data.effectiveFrom.getTime() / 1000), nanoseconds: 0 },
    effectiveUntil: data.effectiveUntil
      ? { seconds: Math.floor(data.effectiveUntil.getTime() / 1000), nanoseconds: 0 }
      : undefined,
  });
  return (result.data as { scheduleId: string }).scheduleId;
};

/**
 * Update a recurring schedule (via Cloud Function)
 */
export const updateRecurringSchedule = async (data: {
  scheduleId: string;
  companyId: string;
  title?: string;
  description?: string;
  location?: {
    address: string;
    latitude: number;
    longitude: number;
  };
  hourlyRate?: number;
  recurrenceFrequency?: RecurrenceFrequency;
  daysOfWeek?: number[];
  startTime?: string;
  endTime?: string;
  effectiveUntil?: Date | null;
  isActive?: boolean;
}): Promise<void> => {
  const updateScheduleFn = httpsCallable(functions, 'updateRecurringSchedule');
  await updateScheduleFn({
    ...data,
    effectiveUntil: data.effectiveUntil
      ? { seconds: Math.floor(data.effectiveUntil.getTime() / 1000), nanoseconds: 0 }
      : data.effectiveUntil === null
      ? null
      : undefined,
  });
};

/**
 * Delete a recurring schedule (via Cloud Function)
 */
export const deleteRecurringSchedule = async (
  scheduleId: string,
  companyId: string,
): Promise<void> => {
  const deleteScheduleFn = httpsCallable(functions, 'deleteRecurringSchedule');
  await deleteScheduleFn({ scheduleId, companyId });
};

/**
 * Assign recurring schedule to manager or staff (via Cloud Function)
 */
export const assignRecurringSchedule = async (data: {
  scheduleId: string;
  companyId: string;
  assignedManagerId?: string;
  userIds?: string[];
}): Promise<void> => {
  const assignScheduleFn = httpsCallable(functions, 'assignRecurringSchedule');
  await assignScheduleFn(data);
};

/**
 * Remove a single staff member from a recurring schedule (via Cloud Function)
 */
export const removeStaffFromSchedule = async (
  scheduleId: string,
  companyId: string,
  staffId: string,
): Promise<void> => {
  const fn = httpsCallable(functions, 'removeStaffFromSchedule');
  await fn({ scheduleId, companyId, staffId });
};

/**
 * Manually trigger job generation for a company (via Cloud Function)
 */
export const generateRecurringJobs = async (companyId?: string): Promise<number> => {
  const generateFn = httpsCallable(functions, 'generateRecurringJobsManual');
  const result = await generateFn({ companyId });
  return (result.data as { generatedCount: number }).generatedCount;
};

/**
 * Helper to format recurrence for display
 * Wrapper around shared utility for backward compatibility
 */
export const formatRecurrence = (schedule: RecurringSchedule): string => {
  return sharedFormatRecurrence(
    schedule.recurrenceFrequency,
    schedule.daysOfWeek,
    schedule.startTime,
    schedule.endTime
  );
};

/**
 * Helper to get day names from day numbers
 */
export const getDayNames = (daysOfWeek: number[]): string[] => {
  return daysOfWeek.map((d) => DAY_NAMES_FULL[d]);
};
